import clsx from "clsx";
import React, { forwardRef } from "react";
import { useVisuallyHidden } from "@react-aria/visually-hidden";
import { ToggleButton as HeadlessToggleButton } from "react-aria-components";

// Imported from their own modules rather than through the package barrel:
// the barrel would close a new import cycle (index -> ToolbarButtons ->
// here -> index) and the repo gates on the cycle count.
import { Icon } from "../../Icon";
import { Spinner } from "../../Spinner";
import { Text } from "../../Text";

// The toggle item has to be a `ToggleButton`, not a `Button`, because only
// the former emits `aria-pressed`. `Button` cannot be re-rooted onto another
// element, so this component renders the toggle element itself and reuses
// `Button`'s stylesheet verbatim — the visual contract lives in exactly one
// place; only the small content subtree below mirrors `Button`'s.
import buttonStyles from "../../Button/src/styles.module.css";
import type { ButtonProps } from "../../Button/src/types";
import type { ForwardedRef } from "react";
import type { Node } from "@react-types/shared";

interface ToolbarToggleButtonProps<T>
  extends Pick<
    ButtonProps,
    | "color"
    | "excludeFromTabOrder"
    | "icon"
    | "iconPosition"
    | "isDisabled"
    | "isLoading"
    | "loadingText"
    | "size"
    | "variant"
  > {
  item: Node<T>;
  /** Controlled pressed state, mirrored to `aria-pressed`. */
  isSelected: boolean;
  /** Fired on press; the consumer owns the resulting state change. */
  onChange: () => void;
  /** Accessible name for an icon-only toggle (see ToolbarButtonsItem). */
  "aria-label"?: string;
}

const _ToolbarToggleButton = <T extends object>(
  props: ToolbarToggleButtonProps<T>,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const {
    color = "accent",
    excludeFromTabOrder = false,
    icon,
    iconPosition = "start",
    isDisabled = false,
    isLoading = false,
    isSelected,
    item,
    loadingText = "Loading...",
    onChange,
    size = "medium",
    variant = "filled",
  } = props;
  const { visuallyHiddenProps } = useVisuallyHidden();
  const hasLabel = Boolean(item.rendered);

  return (
    <HeadlessToggleButton
      aria-label={props["aria-label"]}
      className={clsx(buttonStyles.button)}
      data-button=""
      data-color={color}
      data-icon-position={iconPosition === "start" ? "start" : "end"}
      data-loading={isLoading ? "" : undefined}
      data-size={Boolean(size) ? size : undefined}
      data-variant={variant}
      excludeFromTabOrder={excludeFromTabOrder}
      isDisabled={isDisabled}
      isSelected={isSelected}
      // A loading button is visually disabled but stays focusable, so its
      // press must not toggle anything — same guard `Button` applies.
      onChange={isLoading ? undefined : onChange}
      ref={ref}
    >
      <span aria-hidden={isLoading ? true : undefined} data-content="">
        {icon && <Icon name={icon} size={size} />}
        {hasLabel && (
          <Text
            data-text=""
            fontWeight={500}
            lineClamp={1}
            size={size === "xSmall" || size === "small" ? "caption" : "body"}
          >
            {item.rendered}
          </Text>
        )}
        {/*
          Keeps an icon-only toggle the same height as a labelled one.
          See the styles for the data-empty-text attribute.
        */}
        {!hasLabel && (
          <Text
            data-empty-text=""
            size={size === "xSmall" || size === "small" ? "caption" : "body"}
          >
            &#8203;
          </Text>
        )}
      </span>

      {isLoading && (
        <span aria-hidden={!isLoading ? true : undefined} data-loader="">
          <Spinner size={size} />
          <span {...visuallyHiddenProps}>{loadingText}</span>
        </span>
      )}
    </HeadlessToggleButton>
  );
};

export const ToolbarToggleButton = forwardRef(_ToolbarToggleButton);
